import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  AttachMoney,
  CardMembership,
  Close,
  CreditCard,
  Delete,
  DirectionsCar,
  Edit,
  History,
  Payment,
  PauseCircle,
  PlayCircle,
  Search,
  Visibility,
  QrCode2,
  Print,
} from "@mui/icons-material";
import { Checkbox, FormControlLabel } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import apiClient from "../../../utils/axios";
import { enqueueSnackbar } from "notistack";
import QRCode from "qrcode";

const TIPOS_ID = [
  { value: "cedula", label: "Cédula" },
  { value: "ruc", label: "RUC" },
  { value: "pasaporte", label: "Pasaporte" },
];

const FORM_VACIO = {
  plan_id: "",
  identificacion: "",
  nombre_titular: "",
  tipo_identificacion: "cedula",
  fecha_inicio: new Date().toISOString().slice(0, 10),
  fecha_fin: "",
  correo: "",
  telefono: "",
  observaciones: "",
};

// ─── Validación de formato de placa ───────────────────────────────────────────
const PLACA_REGEX = /^([A-Z]{2}\d{4}|[A-Z]{3}\d{3,4})$/;
const validarPlaca = (p) => PLACA_REGEX.test(p);
const normalizarPlaca = (v) => v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);

// ─── Input de placas locales (antes de guardar) ───────────────────────────────
const PlacasInputLocal = ({ placas, onChange }) => {
  const [nueva, setNueva] = useState("");
  const [error, setError] = useState("");

  const agregar = () => {
    const p = nueva.trim();
    if (!p) return;
    if (!validarPlaca(p)) {
      setError("Formato inválido. Usa: AB1234, ABC123 o ABC1234");
      return;
    }
    if (placas.includes(p)) {
      setError("Esa placa ya está en la lista");
      return;
    }
    onChange([...placas, p]);
    setNueva("");
    setError("");
  };

  return (
    <Box>
      <Box display="flex" gap={1} mb={error ? 0.5 : 1.5} alignItems="flex-start">
        <TextField
          size="small"
          placeholder="Ej: ABC1234"
          value={nueva}
          error={!!error}
          onChange={(e) => { setNueva(normalizarPlaca(e.target.value)); setError(""); }}
          onKeyDown={(e) => e.key === "Enter" && agregar()}
          sx={{ width: 180 }}
          inputProps={{ style: { textTransform: "uppercase", fontWeight: 600, letterSpacing: 1 }, maxLength: 7 }}
        />
        <Button
          size="small"
          variant="contained"
          startIcon={<Add />}
          onClick={agregar}
          disabled={!nueva.trim()}
          sx={{ mt: 0.1 }}
        >
          Agregar
        </Button>
      </Box>
      {error && <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>{error}</Typography>}

      <Box
        display="flex"
        gap={1}
        flexWrap="wrap"
        minHeight={40}
        sx={{
          bgcolor: placas.length > 0 ? "primary.50" : "transparent",
          borderRadius: 1,
          p: placas.length > 0 ? 1 : 0,
        }}
      >
        {placas.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
            Sin placas — agregue al menos una
          </Typography>
        ) : (
          placas.map((p) => (
            <Chip
              key={p}
              label={p}
              icon={<DirectionsCar />}
              onDelete={() => onChange(placas.filter((x) => x !== p))}
              sx={{
                fontWeight: 700, letterSpacing: 0.5, fontSize: "0.85rem", px: 0.5,
                bgcolor: "grey.800", color: "white",
                "& .MuiChip-icon": { color: "grey.300" },
                "& .MuiChip-deleteIcon": { color: "grey.400", "&:hover": { color: "white" } },
              }}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

// ─── Gestión de placas de una suscripción (con API) ───────────────────────────
const PlacasSuscripcion = ({ suscripcionId, readOnly = false, autoAbrirQR = false, mostrarBotonQR = true }) => {
  const [placas, setPlacas] = useState([]);
  const [nueva, setNueva] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorPlaca, setErrorPlaca] = useState("");
  const [qrPlaca, setQrPlaca] = useState(null);
  const autoAbrioRef = useRef(false);

  useEffect(() => {
    if (!suscripcionId) return;
    apiClient
      .get(`/api/planes/suscripciones/${suscripcionId}/placas`)
      .then((r) => {
        if (r.data?.success) {
          setPlacas(r.data.data);
          // Si las placas se crearon antes de esta pantalla (p.ej. junto con
          // el suscriptor, en un solo paso), mostrar el QR de la primera de
          // una vez — sin esperar a que alguien haga clic en un ícono.
          if (autoAbrirQR && !autoAbrioRef.current && r.data.data.length > 0) {
            setQrPlaca(r.data.data[0]);
            autoAbrioRef.current = true;
          }
        }
      })
      .catch(() => {});
  }, [suscripcionId]);

  const agregar = async () => {
    const p = nueva.trim();
    if (!p) return;
    if (!validarPlaca(p)) {
      setErrorPlaca("Formato inválido. Usa: AB1234, ABC123 o ABC1234");
      return;
    }
    if (placas.some(x => x.numero_placa === p)) {
      setErrorPlaca("Esa placa ya está registrada");
      return;
    }
    setErrorPlaca("");
    setLoading(true);
    try {
      const r = await apiClient.post(
        `/api/planes/suscripciones/${suscripcionId}/placas`,
        { numero_placa: p }
      );
      if (r.data?.success) {
        setPlacas((prev) => [...prev, r.data.data]);
        setNueva("");
        enqueueSnackbar("Placa agregada", { variant: "success" });
        // Ofrecer imprimir el QR de inmediato — se puede volver a abrir/
        // reimprimir en cualquier momento con el ícono junto a la placa.
        setQrPlaca(r.data.data);
      }
    } catch {
      enqueueSnackbar("Error al agregar placa", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const eliminar = async (id) => {
    try {
      await apiClient.delete(`/api/planes/suscripciones/placas/${id}`);
      setPlacas((prev) => prev.filter((p) => p.id !== id));
      enqueueSnackbar("Placa eliminada", { variant: "warning" });
    } catch {
      enqueueSnackbar("Error al eliminar", { variant: "error" });
    }
  };

  return (
    <Box mt={2}>
      <Divider sx={{ mb: 1.5 }} />
      <Box display="flex" alignItems="center" gap={0.5} mb={1.5}>
        <DirectionsCar fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight="bold">Placas asociadas</Typography>
        <Chip label={placas.length} size="small" color="primary" sx={{ ml: 0.5, height: 18, fontSize: "0.7rem" }} />
        <Typography variant="caption" color="text.secondary" ml={1}>
          La cámara abrirá automáticamente si el plan cubre el momento
        </Typography>
      </Box>

      {!readOnly && (
        <Box mb={2}>
          <Box display="flex" gap={1} alignItems="flex-start">
            <TextField
              size="small"
              placeholder="Ej: ABC1234"
              value={nueva}
              error={!!errorPlaca}
              onChange={(e) => { setNueva(normalizarPlaca(e.target.value)); setErrorPlaca(""); }}
              onKeyDown={(e) => e.key === "Enter" && agregar()}
              sx={{ width: 190 }}
              inputProps={{ style: { textTransform: "uppercase", fontWeight: 600, letterSpacing: 1 }, maxLength: 7 }}
            />
            <Button
              size="small"
              variant="contained"
              startIcon={loading ? <CircularProgress size={14} /> : <Add />}
              onClick={agregar}
              disabled={loading || !nueva.trim()}
              sx={{ mt: 0.1 }}
            >
              Agregar placa
            </Button>
          </Box>
          {errorPlaca && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{errorPlaca}</Typography>}
        </Box>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: placas.length === 0 ? 1.5 : 2,
          borderRadius: 2,
          bgcolor: placas.length > 0 ? "primary.50" : "action.hover",
          borderColor: placas.length > 0 ? "primary.200" : "divider",
          minHeight: 52,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {placas.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            {readOnly ? "Sin placas registradas" : "Aún no hay placas — escriba una arriba y presione Enter o «Agregar placa»"}
          </Typography>
        ) : (
          placas.map((p) => (
            <Box key={p.id} display="flex" alignItems="center" gap={0.25}>
              <Chip
                label={p.numero_placa}
                icon={<DirectionsCar />}
                onDelete={readOnly ? undefined : () => eliminar(p.id)}
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  fontSize: "0.82rem",
                  height: 32,
                  bgcolor: "grey.800",
                  color: "white",
                  "& .MuiChip-icon": { fontSize: "1rem", color: "grey.300" },
                  "& .MuiChip-deleteIcon": { color: "grey.400", "&:hover": { color: "white" } },
                }}
              />
              {mostrarBotonQR && p.codigo_qr && (
                <Tooltip title="Ver / imprimir QR de salida">
                  <IconButton size="small" onClick={() => setQrPlaca(p)}>
                    <QrCode2 fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))
        )}
      </Paper>

      <QrPlacaDialog placa={qrPlaca} onClose={() => setQrPlaca(null)} />
    </Box>
  );
};

// ─── QR fijo de una placa (para marcar la salida) ─────────────────────────────
const QrPlacaDialog = ({ placa, onClose }) => {
  const [qrDataUrl, setQrDataUrl] = useState(null);

  useEffect(() => {
    if (!placa?.codigo_qr) { setQrDataUrl(null); return; }
    QRCode.toDataURL(placa.codigo_qr, {
      width: 512,
      margin: 1,
      errorCorrectionLevel: "H",
      color: { dark: "#000000", light: "#FFFFFF" },
    }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
  }, [placa]);

  const imprimir = () => {
    if (!qrDataUrl || !placa) return;
    const w = window.open("", "_blank", "width=400,height=600");
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>QR de salida</title><style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:Arial,Helvetica,sans-serif;width:54mm;margin:0 auto;padding:3mm;text-align:center}
      .placa{font-family:'Courier New',monospace;font-size:16pt;font-weight:900;letter-spacing:1px;margin-bottom:2mm}
      .caption{font-size:8pt;font-weight:bold;margin:2mm 0}
      img{width:100%;max-width:50mm}
      @media print{@page{size:58mm auto;margin:0}}
    </style></head><body>
      <div class="placa">${placa.numero_placa}</div>
      <img src="${qrDataUrl}" alt="QR"/>
      <div class="caption">Escanear al salir</div>
      <div class="caption">${placa.codigo_qr}</div>
      <script>window.onload=function(){setTimeout(function(){window.print();window.close();},500);};</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <Dialog open={!!placa} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        QR de salida — {placa?.numero_placa}
        <IconButton size="small" onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box textAlign="center" py={1}>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="QR" style={{ width: 220, height: 220 }} />
          ) : (
            <CircularProgress size={32} />
          )}
          <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
            Este QR es fijo para esta placa. Al escanearlo en la salida, se aplica
            automáticamente el plan/tarjeta: si cubre el momento, sale sin costo;
            si no, se abre el cobro correspondiente.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Puedes volver a imprimirlo cuando quieras con el ícono <QrCode2 sx={{ fontSize: 14, verticalAlign: "middle" }} /> junto a la placa.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button variant="contained" startIcon={<Print />} onClick={imprimir} disabled={!qrDataUrl}>
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Formulario de suscripción ────────────────────────────────────────────────
const SuscripcionForm = ({ form, onChange, planes, errores = {} }) => (
  <Grid container spacing={2} sx={{ mt: 0.5 }}>
    <Grid item xs={12} sm={6}>
      <TextField
        size="small" fullWidth required
        label="Nombre del titular"
        value={form.nombre_titular}
        onChange={(e) => onChange({ ...form, nombre_titular: e.target.value })}
        error={!!errores.nombre_titular}
        helperText={errores.nombre_titular}
      />
    </Grid>
    <Grid item xs={12} sm={3}>
      <FormControl size="small" fullWidth required>
        <InputLabel>Tipo ID</InputLabel>
        <Select
          label="Tipo ID"
          value={form.tipo_identificacion}
          onChange={(e) => onChange({ ...form, tipo_identificacion: e.target.value })}
        >
          {TIPOS_ID.map((t) => (
            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
    <Grid item xs={12} sm={3}>
      <TextField
        size="small" fullWidth required
        label="Identificación"
        value={form.identificacion}
        onChange={(e) => onChange({ ...form, identificacion: e.target.value })}
        error={!!errores.identificacion}
        helperText={errores.identificacion}
      />
    </Grid>

    {/* Plan — full width para que quepa bien */}
    <Grid item xs={12}>
      <FormControl size="small" fullWidth required error={!!errores.plan_id}>
        <InputLabel shrink>Plan contratado</InputLabel>
        <Select
          label="Plan contratado"
          displayEmpty
          notched
          value={form.plan_id}
          onChange={(e) => onChange({ ...form, plan_id: e.target.value })}
          renderValue={(selected) => {
            const p = planes.find((pl) => pl.id === selected);
            if (!p) return <Typography variant="body2" color="text.disabled">— Seleccione un plan —</Typography>;
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  label={p.tipo}
                  size="small"
                  color={p.tipo === "tarjeta" ? "secondary" : p.tipo === "mensual" ? "primary" : "default"}
                  sx={{ height: 20, fontSize: "0.65rem", textTransform: "capitalize" }}
                />
                <span style={{ fontWeight: 600 }}>{p.nombre}</span>
                <Typography variant="caption" color="text.secondary">
                  {p.tipo === "tarjeta"
                    ? `${p.horas_prepago}h prepago`
                    : `$${p.precio_con_iva?.toFixed(2)}${p.aplica_iva ? " c/IVA" : ""}`}
                </Typography>
              </Box>
            );
          }}
        >
          {planes.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              <Box display="flex" alignItems="center" gap={1.5} width="100%">
                <Chip
                  label={p.tipo}
                  size="small"
                  color={p.tipo === "tarjeta" ? "secondary" : p.tipo === "mensual" ? "primary" : "default"}
                  sx={{ height: 20, fontSize: "0.65rem", textTransform: "capitalize", minWidth: 62 }}
                />
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={600}>{p.nombre}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.dias_nombre?.join(", ")}
                    {p.todo_el_dia ? " · Todo el día" : p.hora_inicio ? ` · ${p.hora_inicio}–${p.hora_fin}` : ""}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={700} color={p.tipo === "tarjeta" ? "secondary.main" : "primary.main"}>
                  {p.tipo === "tarjeta"
                    ? `${p.horas_prepago}h`
                    : `$${p.precio_con_iva?.toFixed(2)}`}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
        {errores.plan_id && (
          <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>
            {errores.plan_id}
          </Typography>
        )}
      </FormControl>
    </Grid>

    <Grid item xs={12} sm={3}>
      <TextField
        size="small" fullWidth required
        label="Fecha inicio" type="date"
        value={form.fecha_inicio}
        onChange={(e) => onChange({ ...form, fecha_inicio: e.target.value })}
        InputLabelProps={{ shrink: true }}
      />
    </Grid>
    <Grid item xs={12} sm={3}>
      <TextField
        size="small" fullWidth
        label="Fecha fin (vacío = sin límite)" type="date"
        value={form.fecha_fin}
        onChange={(e) => onChange({ ...form, fecha_fin: e.target.value })}
        InputLabelProps={{ shrink: true }}
      />
    </Grid>
    <Grid item xs={12} sm={3}>
      <TextField
        size="small" fullWidth
        label="Correo electrónico"
        value={form.correo}
        onChange={(e) => onChange({ ...form, correo: e.target.value })}
      />
    </Grid>
    <Grid item xs={12} sm={3}>
      <TextField
        size="small" fullWidth
        label="Teléfono"
        value={form.telefono}
        onChange={(e) => onChange({ ...form, telefono: e.target.value })}
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        size="small" fullWidth
        label="Observaciones"
        value={form.observaciones}
        onChange={(e) => onChange({ ...form, observaciones: e.target.value })}
      />
    </Grid>
  </Grid>
);

// ─── Panel de solo lectura ────────────────────────────────────────────────────
const VistaDetalle = ({ sub, planes }) => {
  const labelTipoId = (tipo) => TIPOS_ID.find((t) => t.value === tipo)?.label || tipo;
  const planInfo = planes.find((p) => p.id === sub.plan_id) || sub.plan;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary">Titular</Typography>
          <Typography variant="body2" fontWeight="bold">{sub.nombre_titular}</Typography>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Typography variant="caption" color="text.secondary">Tipo identificación</Typography>
          <Typography variant="body2">{labelTipoId(sub.tipo_identificacion)}</Typography>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Typography variant="caption" color="text.secondary">Identificación</Typography>
          <Typography variant="body2">{sub.identificacion}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">Plan contratado</Typography>
          <Box display="flex" alignItems="center" gap={1} mt={0.25} flexWrap="wrap">
            <Chip
              label={planInfo?.tipo || ""}
              size="small"
              color={planInfo?.tipo === "tarjeta" ? "secondary" : planInfo?.tipo === "mensual" ? "primary" : "default"}
              sx={{ textTransform: "capitalize" }}
            />
            <Typography variant="body2" fontWeight={600}>{planInfo?.nombre || `Plan #${sub.plan_id}`}</Typography>
            <Typography variant="caption" color="text.secondary">
              {planInfo?.tipo === "tarjeta"
                ? `${planInfo.horas_prepago}h prepago`
                : `$${planInfo?.precio_con_iva?.toFixed(2)}${planInfo?.aplica_iva ? " c/IVA" : ""}`}
            </Typography>
          </Box>
          {planInfo?.dias_nombre && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
              Días: {planInfo.dias_nombre.join(", ")}
              {planInfo.todo_el_dia ? " · Todo el día" : planInfo.hora_inicio ? ` · ${planInfo.hora_inicio}–${planInfo.hora_fin}` : ""}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={3}>
          <Typography variant="caption" color="text.secondary">Fecha inicio</Typography>
          <Typography variant="body2">{sub.fecha_inicio}</Typography>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Typography variant="caption" color="text.secondary">Fecha fin</Typography>
          <Typography variant="body2">{sub.fecha_fin || "Sin vencimiento"}</Typography>
        </Grid>
        {sub.correo && (
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Correo</Typography>
            <Typography variant="body2">{sub.correo}</Typography>
          </Grid>
        )}
        {sub.telefono && (
          <Grid item xs={12} sm={3}>
            <Typography variant="caption" color="text.secondary">Teléfono</Typography>
            <Typography variant="body2">{sub.telefono}</Typography>
          </Grid>
        )}
        {sub.observaciones && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Observaciones</Typography>
            <Typography variant="body2">{sub.observaciones}</Typography>
          </Grid>
        )}
      </Grid>
      <PlacasSuscripcion suscripcionId={sub.id} readOnly />
    </Box>
  );
};

// ─── Tarjeta prepago ──────────────────────────────────────────────────────────
const TarjetaPrepago = ({ suscripcionId, readOnly = false }) => {
  const [tarjeta, setTarjeta] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [horasRecarga, setHorasRecarga] = useState("");
  const [descRecarga, setDescRecarga] = useState("");
  const [loading, setLoading] = useState(false);
  const [showMovs, setShowMovs] = useState(false);
  const [creandoNueva, setCreandoNueva] = useState(false);
  const [horasNueva, setHorasNueva] = useState("");

  const cargarTarjeta = useCallback(async () => {
    try {
      const r = await apiClient.get(`/api/planes/suscripciones/${suscripcionId}/tarjeta`);
      if (r.data?.success) setTarjeta(r.data.data);
    } catch {}
  }, [suscripcionId]);

  useEffect(() => { if (suscripcionId) cargarTarjeta(); }, [suscripcionId, cargarTarjeta]);

  const cargarMovimientos = async () => {
    if (!tarjeta) return;
    try {
      const r = await apiClient.get(`/api/planes/tarjetas/${tarjeta.id}/movimientos`);
      if (r.data?.success) setMovimientos(r.data.data);
      setShowMovs(true);
    } catch { enqueueSnackbar("Error al cargar movimientos", { variant: "error" }); }
  };

  const crearTarjeta = async () => {
    const horas = parseFloat(horasNueva);
    if (!horas || horas <= 0) return;
    setLoading(true);
    try {
      const r = await apiClient.post(`/api/planes/suscripciones/${suscripcionId}/tarjeta`, { horas_totales: horas });
      if (r.data?.success) { setTarjeta(r.data.data); setCreandoNueva(false); setHorasNueva(""); enqueueSnackbar("Tarjeta creada", { variant: "success" }); }
    } catch { enqueueSnackbar("Error al crear tarjeta", { variant: "error" }); }
    finally { setLoading(false); }
  };

  const recargar = async () => {
    const horas = parseFloat(horasRecarga);
    if (!horas || horas <= 0 || !tarjeta) return;
    setLoading(true);
    try {
      const r = await apiClient.post(`/api/planes/tarjetas/${tarjeta.id}/recargar`, { horas, descripcion: descRecarga || "Recarga manual" });
      if (r.data?.success) { setTarjeta(r.data.data); setHorasRecarga(""); setDescRecarga(""); enqueueSnackbar(`+${horas}h recargadas`, { variant: "success" }); }
    } catch { enqueueSnackbar("Error al recargar", { variant: "error" }); }
    finally { setLoading(false); }
  };

  const pct = tarjeta ? Math.min(100, Math.max(0, (tarjeta.horas_restantes / tarjeta.horas_totales) * 100)) : 0;
  const colorBarra = pct > 40 ? "success" : pct > 15 ? "warning" : "error";

  return (
    <Box mt={2}>
      <Divider sx={{ mb: 1.5 }} />
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <CreditCard fontSize="small" color="secondary" />
        <Typography variant="subtitle2" fontWeight="bold">Tarjeta Prepago de Horas</Typography>
      </Box>

      {!tarjeta && !creandoNueva && (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary">Sin tarjeta prepago asignada.</Typography>
          {!readOnly && (
            <Button size="small" startIcon={<Add />} onClick={() => setCreandoNueva(true)}>Crear tarjeta</Button>
          )}
        </Box>
      )}

      {!tarjeta && creandoNueva && (
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField size="small" label="Horas iniciales" type="number" value={horasNueva}
            onChange={e => setHorasNueva(e.target.value)} sx={{ width: 150 }} inputProps={{ min: 1, step: 0.5 }} />
          <Button size="small" variant="contained" onClick={crearTarjeta} disabled={loading || !horasNueva}>Crear</Button>
          <Button size="small" onClick={() => setCreandoNueva(false)}>Cancelar</Button>
        </Box>
      )}

      {tarjeta && (
        <Box>
          <Box display="flex" gap={3} alignItems="center" flexWrap="wrap" mb={0.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">Total asignado</Typography>
              <Typography variant="body2" fontWeight="bold">{tarjeta.horas_totales}h</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Usadas</Typography>
              <Typography variant="body2">{tarjeta.horas_usadas}h</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Restantes</Typography>
              <Typography variant="body2" fontWeight="bold" color={colorBarra + ".main"}>
                {tarjeta.horas_restantes}h
              </Typography>
            </Box>
            {tarjeta.deuda_horas > 0 && (
              <Chip label={`Deuda: ${tarjeta.deuda_horas}h — pagar en caja`} size="small" color="error" />
            )}
            <Tooltip title="Ver movimientos">
              <IconButton size="small" onClick={cargarMovimientos}><History fontSize="small" /></IconButton>
            </Tooltip>
          </Box>

          <LinearProgress variant="determinate" value={pct} color={colorBarra} sx={{ borderRadius: 1, height: 6, mb: 1 }} />

          {tarjeta.saldo_agotado && tarjeta.deuda_horas === 0 && (
            <Alert severity="warning" sx={{ py: 0.3, mb: 1 }}>Saldo agotado. El vehículo deberá pagar en caja.</Alert>
          )}

          {!readOnly && (
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mt={1}>
              <TextField size="small" label="Horas a recargar" type="number" value={horasRecarga}
                onChange={e => setHorasRecarga(e.target.value)} sx={{ width: 160 }} inputProps={{ min: 0.5, step: 0.5 }} />
              <TextField size="small" label="Descripción (opcional)" value={descRecarga}
                onChange={e => setDescRecarga(e.target.value)} sx={{ width: 200 }} />
              <Button size="small" variant="outlined" color="success" onClick={recargar}
                disabled={loading || !horasRecarga} startIcon={loading ? <CircularProgress size={14} /> : <Add />}>
                Recargar
              </Button>
            </Box>
          )}

          {showMovs && (
            <Box mt={1.5}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary">Historial de movimientos</Typography>
              <Table size="small" sx={{ mt: 0.5 }}>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Fecha</strong></TableCell>
                    <TableCell><strong>Tipo</strong></TableCell>
                    <TableCell><strong>Horas</strong></TableCell>
                    <TableCell><strong>Saldo antes</strong></TableCell>
                    <TableCell><strong>Saldo después</strong></TableCell>
                    <TableCell><strong>Placa</strong></TableCell>
                    <TableCell><strong>Descripción</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movimientos.length === 0 && (
                    <TableRow><TableCell colSpan={7} align="center"><Typography variant="caption" color="text.secondary">Sin movimientos</Typography></TableCell></TableRow>
                  )}
                  {movimientos.map(m => (
                    <TableRow key={m.id}>
                      <TableCell><Typography variant="caption">{new Date(m.created_at).toLocaleString('es-EC')}</Typography></TableCell>
                      <TableCell>
                        <Chip label={m.tipo} size="small"
                          color={m.tipo === 'recarga' ? 'success' : m.tipo === 'descuento' ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color={m.horas >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                          {m.horas >= 0 ? '+' : ''}{m.horas}h
                        </Typography>
                      </TableCell>
                      <TableCell><Typography variant="caption">{m.horas_antes}h</Typography></TableCell>
                      <TableCell><Typography variant="caption">{m.horas_despues}h</Typography></TableCell>
                      <TableCell><Typography variant="caption">{m.numero_placa || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{m.descripcion}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button size="small" sx={{ mt: 0.5 }} onClick={() => setShowMovs(false)}>Ocultar historial</Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const PlanSubscribersDialog = ({ open, onClose }) => {
  const [suscripciones, setSuscripciones] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState("");

  const [panel, setPanel] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [placasPendientes, setPlacasPendientes] = useState([]);
  const [formEdit, setFormEdit] = useState(null);
  const [errores, setErrores] = useState({});

  // Estado del cobro al crear suscripción
  const [registrarCobro, setRegistrarCobro] = useState(true);
  const [cobroMetodo, setCobroMetodo] = useState("efectivo");
  const [cobroMonto, setCobroMonto] = useState("");

  const cerrarPanel = () => {
    setPanel(null); setFormEdit(null); setErrores({});
    setCobroMonto(""); setCobroMetodo("efectivo");
  };

  // Auto-rellenar monto con el precio del plan al seleccionarlo
  useEffect(() => {
    if (!form.plan_id) return;
    const plan = planes.find((p) => p.id === form.plan_id);
    if (plan && plan.tipo !== "tarjeta") {
      setCobroMonto(String(plan.precio_con_iva ?? plan.precio ?? ""));
    } else {
      setCobroMonto("");
    }
  }, [form.plan_id, planes]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [resSubs, resPlanes] = await Promise.all([
        apiClient.get("/api/planes/suscripciones"),
        apiClient.get("/api/planes?solo_activos=true"),
      ]);
      if (resSubs.data?.success) setSuscripciones(resSubs.data.data);
      if (resPlanes.data?.success) setPlanes(resPlanes.data.data);
    } catch {
      enqueueSnackbar("Error al cargar suscripciones", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) cargar();
  }, [open, cargar]);

  const validar = (f) => {
    const e = {};
    if (!f.nombre_titular?.trim()) e.nombre_titular = "Requerido";
    if (!f.identificacion?.trim()) e.identificacion = "Requerido";
    if (!f.plan_id) e.plan_id = "Selecciona un plan";
    return e;
  };

  const handleCrear = async () => {
    const e = validar(form);
    if (Object.keys(e).length) { setErrores(e); return; }
    try {
      const res = await apiClient.post("/api/planes/suscripciones", {
        ...form,
        fecha_fin: form.fecha_fin || null,
      });
      if (res.data?.success) {
        const nueva = res.data.data;

        // Guardar placas pendientes una a una
        if (placasPendientes.length > 0) {
          await Promise.allSettled(
            placasPendientes.map((placa) =>
              apiClient.post(`/api/planes/suscripciones/${nueva.id}/placas`, { numero_placa: placa })
            )
          );
        }

        // Registrar cobro en caja si el usuario lo solicitó
        if (registrarCobro) {
          const monto = parseFloat(cobroMonto);
          if (monto > 0) {
            try {
              await apiClient.post(`/api/planes/suscripciones/${nueva.id}/cobrar`, {
                metodo: cobroMetodo,
                monto,
                descripcion: `Suscripción ${nueva.plan?.nombre || ""} · ${nueva.nombre_titular}`,
              });
              enqueueSnackbar(`Cobro de $${monto.toFixed(2)} registrado en caja (${cobroMetodo})`, {
                variant: "info",
              });
            } catch {
              enqueueSnackbar("Suscripción creada, pero no se pudo registrar el cobro en caja", {
                variant: "warning",
              });
            }
          }
        }

        await cargar(); // recargar para tener datos frescos
        setForm(FORM_VACIO);
        setPlacasPendientes([]);
        setErrores({});
        setCobroMonto("");
        setCobroMetodo("efectivo");
        // Abrir en edición para agregar/gestionar más placas
        setPanel({ modo: "editar", sub: nueva });
        setFormEdit({
          id: nueva.id,
          plan_id: nueva.plan_id,
          nombre_titular: nueva.nombre_titular,
          tipo_identificacion: nueva.tipo_identificacion,
          identificacion: nueva.identificacion,
          fecha_inicio: nueva.fecha_inicio || "",
          fecha_fin: nueva.fecha_fin || "",
          correo: nueva.correo || "",
          telefono: nueva.telefono || "",
          observaciones: nueva.observaciones || "",
        });
        enqueueSnackbar(
          placasPendientes.length > 0
            ? `Suscripción creada con ${placasPendientes.length} placa(s)`
            : "Suscripción creada",
          { variant: "success" }
        );
      }
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || "Error al crear", { variant: "error" });
    }
  };

  const handleActualizar = async () => {
    if (!formEdit) return;
    const e = validar(formEdit);
    if (Object.keys(e).length) { setErrores(e); return; }
    try {
      const { id, ...datos } = formEdit;
      const res = await apiClient.put(`/api/planes/suscripciones/${id}`, {
        ...datos,
        fecha_fin: datos.fecha_fin || null,
      });
      if (res.data?.success) {
        setSuscripciones((prev) =>
          prev.map((s) => (s.id === id ? res.data.data : s))
        );
        cerrarPanel();
        enqueueSnackbar("Suscripción actualizada", { variant: "success" });
      }
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || "Error al actualizar", { variant: "error" });
    }
  };

  const handleToggleActivo = async (sub) => {
    try {
      const res = await apiClient.put(`/api/planes/suscripciones/${sub.id}`, {
        activo: !sub.activo,
      });
      if (res.data?.success) {
        setSuscripciones((prev) =>
          prev.map((s) => (s.id === sub.id ? { ...s, activo: !sub.activo } : s))
        );
        if (panel?.sub?.id === sub.id) {
          setPanel((p) => ({ ...p, sub: { ...p.sub, activo: !sub.activo } }));
        }
        enqueueSnackbar(
          sub.activo ? "Suscripción desactivada" : "Suscripción activada",
          { variant: sub.activo ? "warning" : "success" }
        );
      }
    } catch {
      enqueueSnackbar("Error al cambiar estado", { variant: "error" });
    }
  };

  const iniciarEdicion = (sub) => {
    setPanel({ modo: "editar", sub });
    setFormEdit({
      id: sub.id,
      plan_id: sub.plan_id,
      nombre_titular: sub.nombre_titular,
      tipo_identificacion: sub.tipo_identificacion,
      identificacion: sub.identificacion,
      fecha_inicio: sub.fecha_inicio || "",
      fecha_fin: sub.fecha_fin || "",
      correo: sub.correo || "",
      telefono: sub.telefono || "",
      observaciones: sub.observaciones || "",
    });
    setErrores({});
  };

  const filtradas = suscripciones.filter((s) => {
    const q = busqueda.toLowerCase();
    return (
      s.nombre_titular?.toLowerCase().includes(q) ||
      s.identificacion?.toLowerCase().includes(q) ||
      s.plan?.nombre?.toLowerCase().includes(q)
    );
  });

  const labelTipoId = (tipo) => TIPOS_ID.find((t) => t.value === tipo)?.label || tipo;
  const panelSubId = panel?.sub?.id;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: "#495057", color: "white",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <CardMembership />
          <Box>
            <Typography variant="h6" fontWeight={600}>Suscriptores de Planes</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Gestiona las personas suscritas a planes de parqueadero
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Barra de herramientas */}
        <Box display="flex" gap={2} mb={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Buscar por nombre, cédula o plan…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setPanel({ modo: "crear" });
              setForm(FORM_VACIO);
              setPlacasPendientes([]);
              setErrores({});
            }}
          >
            Nueva suscripción
          </Button>
        </Box>

        {/* Panel: crear */}
        {panel?.modo === "crear" && (
          <Paper
            variant="outlined"
            sx={{ borderColor: "primary.main", borderRadius: 2, p: 2.5, mb: 2 }}
          >
            <Typography variant="subtitle1" fontWeight="bold" mb={0.5}>Nueva suscripción</Typography>
            <SuscripcionForm form={form} onChange={setForm} planes={planes} errores={errores} />

            {/* Placas durante la creación */}
            <Box mt={2}>
              <Divider sx={{ mb: 1.5 }} />
              <Box display="flex" alignItems="center" gap={0.5} mb={1.5}>
                <DirectionsCar fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight="bold">Placas del suscriptor</Typography>
                {placasPendientes.length > 0 && (
                  <Chip label={placasPendientes.length} size="small" color="primary" sx={{ height: 18, fontSize: "0.7rem", ml: 0.5 }} />
                )}
              </Box>
              <PlacasInputLocal placas={placasPendientes} onChange={setPlacasPendientes} />
            </Box>

            {/* Cobro en caja */}
            <Box mt={2}>
              <Divider sx={{ mb: 1.5 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={registrarCobro}
                    onChange={(e) => setRegistrarCobro(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="subtitle2" fontWeight="bold">
                    Registrar cobro en caja
                  </Typography>
                }
              />
              {registrarCobro && (
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Monto a cobrar"
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      value={cobroMonto}
                      onChange={(e) => setCobroMonto(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoney fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      helperText="Se rellena automáticamente con el precio del plan"
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Método de pago</InputLabel>
                      <Select
                        label="Método de pago"
                        value={cobroMetodo}
                        onChange={(e) => setCobroMetodo(e.target.value)}
                        startAdornment={
                          <InputAdornment position="start">
                            <Payment fontSize="small" />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="efectivo">Efectivo</MenuItem>
                        <MenuItem value="tarjeta">Tarjeta</MenuItem>
                        <MenuItem value="transferencia">Transferencia</MenuItem>
                        <MenuItem value="de_una">De Una</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2} sx={{ display: "flex", alignItems: "center" }}>
                    {cobroMonto > 0 && (
                      <Typography variant="body2" color="primary" fontWeight={700}>
                        Total: ${Number(cobroMonto).toFixed(2)}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              )}
            </Box>

            <Box display="flex" gap={1} mt={2.5} justifyContent="flex-end">
              <Button size="small" onClick={cerrarPanel}>Cancelar</Button>
              <Button size="small" variant="contained" onClick={handleCrear}>
                Guardar suscripción
              </Button>
            </Box>
          </Paper>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Titular</strong></TableCell>
                <TableCell><strong>Identificación</strong></TableCell>
                <TableCell><strong>Plan</strong></TableCell>
                <TableCell><strong>Vigencia</strong></TableCell>
                <TableCell><strong>Contacto</strong></TableCell>
                <TableCell align="center"><strong>Estado</strong></TableCell>
                <TableCell align="right"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtradas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      {busqueda ? "Sin resultados para la búsqueda" : "No hay suscripciones registradas"}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filtradas.map((sub) => (
                <React.Fragment key={sub.id}>
                  <TableRow
                    sx={{
                      opacity: sub.activo ? 1 : 0.6,
                      bgcolor: panelSubId === sub.id ? "action.selected" : "inherit",
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{sub.nombre_titular}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{labelTipoId(sub.tipo_identificacion)}</Typography>
                      <br />
                      <Typography variant="body2">{sub.identificacion}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                        <Chip
                          label={sub.plan?.tipo || ""}
                          size="small"
                          color={sub.plan?.tipo === "tarjeta" ? "secondary" : sub.plan?.tipo === "mensual" ? "primary" : "default"}
                          sx={{ height: 18, fontSize: "0.62rem", textTransform: "capitalize" }}
                        />
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {sub.plan?.nombre || `Plan #${sub.plan_id}`}
                        </Typography>
                      </Box>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {sub.plan?.tipo === "tarjeta"
                          ? `${sub.plan.horas_prepago}h prepago`
                          : `$${sub.plan?.precio_con_iva?.toFixed(2)}${sub.plan?.aplica_iva ? " c/IVA" : ""}`}
                      </Typography>
                      {sub.plan?.tipo === "tarjeta" && sub.tarjeta && (
                        <Chip
                          label={
                            sub.tarjeta.saldo_agotado
                              ? "Sin saldo"
                              : `${sub.tarjeta.horas_restantes}h restantes`
                          }
                          size="small"
                          color={
                            sub.tarjeta.saldo_agotado
                              ? "error"
                              : sub.tarjeta.horas_restantes / sub.tarjeta.horas_totales > 0.4
                              ? "success"
                              : "warning"
                          }
                          sx={{ height: 16, fontSize: "0.6rem", mt: 0.3 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{sub.fecha_inicio}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sub.fecha_fin ? `→ ${sub.fecha_fin}` : "Sin vencimiento"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {sub.correo && <Typography variant="caption" display="block">{sub.correo}</Typography>}
                      {sub.telefono && <Typography variant="caption" display="block">{sub.telefono}</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={sub.activo ? "Activo" : "Inactivo"}
                        size="small"
                        color={sub.activo ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Ver detalle">
                        <IconButton
                          size="small"
                          color={panel?.modo === "ver" && panelSubId === sub.id ? "primary" : "default"}
                          onClick={() =>
                            panelSubId === sub.id && panel?.modo === "ver"
                              ? cerrarPanel()
                              : setPanel({ modo: "ver", sub })
                          }
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          color={panel?.modo === "editar" && panelSubId === sub.id ? "primary" : "default"}
                          onClick={() =>
                            panelSubId === sub.id && panel?.modo === "editar"
                              ? cerrarPanel()
                              : iniciarEdicion(sub)
                          }
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={sub.activo ? "Desactivar suscripción" : "Activar suscripción"}>
                        <IconButton
                          size="small"
                          color={sub.activo ? "warning" : "success"}
                          onClick={() => handleToggleActivo(sub)}
                        >
                          {sub.activo ? <PauseCircle fontSize="small" /> : <PlayCircle fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {/* Panel inline: ver detalle */}
                  {panel?.modo === "ver" && panelSubId === sub.id && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ bgcolor: "action.hover", p: 0 }}>
                        <Box p={2.5}>
                          <Typography variant="subtitle2" fontWeight="bold" mb={1.5}>
                            Detalle — {sub.nombre_titular}
                          </Typography>
                          <VistaDetalle sub={sub} planes={planes} />
                          <TarjetaPrepago suscripcionId={sub.id} readOnly />
                          <Box display="flex" justifyContent="flex-end" mt={2}>
                            <Button size="small" onClick={cerrarPanel}>Cerrar</Button>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Panel inline: editar */}
                  {panel?.modo === "editar" && panelSubId === sub.id && formEdit && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ bgcolor: "action.hover", p: 0 }}>
                        <Box p={2.5}>
                          <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                            Editando — {sub.nombre_titular}
                          </Typography>
                          <SuscripcionForm
                            form={formEdit}
                            onChange={setFormEdit}
                            planes={planes}
                            errores={errores}
                          />
                          <PlacasSuscripcion suscripcionId={sub.id} />
                          <TarjetaPrepago suscripcionId={sub.id} />
                          <Box display="flex" gap={1} mt={2.5} justifyContent="flex-end">
                            <Button size="small" onClick={cerrarPanel}>Cancelar</Button>
                            <Button size="small" variant="contained" onClick={handleActualizar}>
                              Guardar datos
                            </Button>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanSubscribersDialog;
export { PlacasSuscripcion, PlacasInputLocal, QrPlacaDialog };
